import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { getHolidays, createHoliday, deleteHoliday } from '../services/api';
import Swal from 'sweetalert2';
import { CalendarDays, Plus, Trash2, RefreshCw } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function HolidayCalendarPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear]         = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState({ date: '', name: '', is_recurring: false });
  const [saving, setSaving]     = useState(false);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await getHolidays({ year });
      setHolidays(res.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchHolidays(); }, [year]); // eslint-disable-line

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return;
    setSaving(true);
    try {
      await createHoliday({ date: form.date, name: form.name.trim(), is_recurring: form.is_recurring });
      setForm({ date: '', name: '', is_recurring: false });
      fetchHolidays();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Could not add holiday.', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    const res = await Swal.fire({
      title: 'Remove Holiday?',
      text: `Remove "${name}" from the calendar?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#dc2626',
    });
    if (!res.isConfirmed) return;
    try {
      await deleteHoliday(id);
      fetchHolidays();
    } catch (_) {
      Swal.fire('Error', 'Could not remove holiday.', 'error');
    }
  };

  // Group by month — parse date string directly to avoid timezone shift
  const byMonth = {};
  holidays.forEach(h => {
    const [, , day] = (h.date || '').slice(0, 10).split('-');
    const m = parseInt((h.date || '').slice(5, 7), 10) - 1;
    if (m < 0 || m > 11) return;
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push({ ...h, _day: parseInt(day, 10) });
  });

  return (
    <AppLayout pageTitle="Holiday Calendar">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CalendarDays size={22} color="var(--primary)" />
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-main)' }}>Holiday Calendar</h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                Holidays are excluded from SLA business-hour calculations.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setYear(y => y - 1)} style={yearBtn}>&lsaquo;</button>
            <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-main)', minWidth: 52, textAlign: 'center' }}>{year}</span>
            <button onClick={() => setYear(y => y + 1)} style={yearBtn}>&rsaquo;</button>
          </div>
        </div>

        {/* Add Holiday Form */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-main)' }}>Add Holiday</h3>
          <form onSubmit={handleAdd} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 160px' }}>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <label style={labelStyle}>Holiday Name</label>
              <input
                type="text"
                placeholder="e.g. Christmas Day"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
              <input
                type="checkbox"
                id="recurring"
                checked={form.is_recurring}
                onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="recurring" style={{ fontSize: 13, color: 'var(--text-sub)', cursor: 'pointer', userSelect: 'none' }}>
                Recurring annually
              </label>
            </div>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 8, border: 'none',
                background: 'var(--primary)', color: '#fff',
                fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              <Plus size={14} /> Add Holiday
            </button>
          </form>
        </div>

        {/* Holiday List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : holidays.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <CalendarDays size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>No holidays added for {year}.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {Array.from({ length: 12 }, (_, m) => (
              byMonth[m]?.length ? (
                <div key={m} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 16px', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                    {MONTHS[m]} {year}
                  </div>
                  <div>
                    {byMonth[m].map(h => (
                      <div key={h.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--border-light)', gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8, background: 'var(--primary-light)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>
                            {h._day}
                          </span>
                          <span style={{ fontSize: 9, color: 'var(--primary)', textTransform: 'uppercase', lineHeight: 1.2 }}>
                            {MONTHS[parseInt((h.date || '').slice(5, 7), 10) - 1]}
                          </span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h.name}
                          </div>
                          {h.is_recurring && (
                            <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>Recurring</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDelete(h.id, h.name)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 6, display: 'flex' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const yearBtn = {
  width: 30, height: 30, borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-card)', color: 'var(--text-sub)', cursor: 'pointer',
  fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-main)', fontSize: 13.5, boxSizing: 'border-box' };
